import React, { useRef, useEffect, useContext, useState, useMemo, useCallback } from "react";
import { useEmploymentType } from "../../contexts/EmploymentTypeMasterContext";
import Modal from "../../components/UI/Modal";
import EmploymentTypeForm from "../../components/Form/EmploymentTypeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { AuthContext } from "../../contexts/AuthContext";
import { MoreVertical, AlertTriangle, Search, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteEmploymentType }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteEmploymentType(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const EmploymentTypeMasters = React.memo(() => {
  const buttonRefs = useRef({});
  const { user, roles } = useContext(AuthContext);
  const { employmentTypes, addEmploymentType, updateEmploymentType, deleteEmploymentType, isLoading } = useEmploymentType();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmploymentType, setEditingEmploymentType] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);



  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingEmploymentType(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((employmentType) => {
    setEditingEmploymentType(employmentType);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingEmploymentType(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (employmentType) => {
      setLoading(true);
      try {
        if (editingEmploymentType) {
          // console.log("employment-type payload", employmentType);
        
          // Convert FormData to object
          // console.log(employmentType instanceof FormData)
          const employmentTypeData = employmentType instanceof FormData ? convertFormDataToObject(employmentType) : employmentType;
  
          // console.log("Payload being sent:", { ...editingEmploymentType, ...employmentTypeData });
  
          await updateEmploymentType({ ...editingEmploymentType, ...employmentTypeData });
          setMessage("EmploymentType updated successfully.");
          // setNotification("EmploymentType updated successfully.");
          setNotification({
            message: "EmploymentType updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create employment-type", employmentType)
          await addEmploymentType(employmentType);
          setMessage("EmploymentType added successfully.");
          // setNotification("EmploymenType added successfully.");
          setNotification({
            message: "EmploymenType added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingEmploymentType, addEmploymentType, updateEmploymentType, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const employmentTypeForTable = useMemo(
    () =>
        employmentTypes.map((employmentType) => ({
        ...employmentType,
        name: employmentType?.name || "Unknown Employment Type",
      })),
    [employmentTypes]
  );

  const filteredEmploymentTypes = useMemo(() => {
    return employmentTypeForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [employmentTypeForTable, searchQuery]);

  const handleDeleteEmploymentType = async (id) => {
    try {
      await deleteEmploymentType(id);
      // setNotification("EmploymentType deleted successfully");
      setNotification({
        message: "EmploymentType deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete employment-type");
      setNotification({
        message: "Failed to delete employment-type",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteEmploymentType={handleDeleteEmploymentType}
          />
        ),
      },
    ],
      [
      openMenu,
      openModalForEdit,
      handleDeleteEmploymentType,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Employment-Type Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Employment-Type
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredEmploymentTypes}
            // data={requests.slice().reverse()}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions"]}
            // excludeFilterColumns={excludeFilterColumns}
          />

        </div>
      )}
      
      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingEmploymentType ? "Edit Employment-Type" : "Add Employment-Type"}
      >
        <EmploymentTypeForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingEmploymentType || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default EmploymentTypeMasters;
